import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useStores } from "@/context/StoreContext";
import { StatCard, Loading } from "@/components/common";
import PeriodFilter, { usePeriod, PERIOD_SUFFIX } from "@/components/PeriodFilter";
import { rupiah, numberID } from "@/lib/format";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TrendingUp, ShoppingCart, Package, Receipt } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";

const COLORS = ["#4F46E5", "#10B981", "#F59E0B", "#0EA5E9", "#8B5CF6", "#EC4899", "#64748B"];

export default function SalesAnalytics() {
  const { activeStore, stores } = useStores();
  const { period, start_date, end_date, setPeriod } = usePeriod();
  const [data, setData] = useState(null);
  const params = { store_id: activeStore, period };
  if (period === "custom") { params.start_date = start_date; params.end_date = end_date; }
  const paramKey = JSON.stringify(params);
  useEffect(() => {
    if (period === "custom" && (!start_date || !end_date)) return;
    setData(null);
    api.get("/analytics/sales", { params }).then((r) => setData(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramKey]);
  const storeName = activeStore === "all" ? "Semua Toko" : (stores.find((s) => s.id === activeStore)?.name || "");
  const suf = PERIOD_SUFFIX[period] || "Periode";
  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="analytics-header">Analitik Penjualan</h1>
          <p className="text-sm text-slate-500 mt-1">Data penjualan: {storeName} · {suf}. Stok produk tetap global.</p>
        </div>
        <PeriodFilter period={period} start_date={start_date} end_date={end_date} onChange={setPeriod} />
      </div>
      {!data ? <Loading label="Memuat analitik..." /> : (<AnalyticsBody data={data} />)}
    </div>
  );
}

function AnalyticsBody({ data }) {
  const s = data.summary;
  return (
    <div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total Revenue" value={rupiah(s.revenue)} icon={TrendingUp} accent="emerald" testId="an-revenue" />
        <StatCard label="Total Pesanan" value={numberID(s.orders)} icon={ShoppingCart} accent="indigo" testId="an-orders" />
        <StatCard label="PCS Terjual" value={numberID(s.pcs)} icon={Package} accent="sky" testId="an-pcs" />
        <StatCard label="Avg Order Value" value={rupiah(s.aov)} icon={Receipt} accent="amber" testId="an-aov" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="Revenue per Marketplace">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.by_marketplace} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `${v / 1000}k`} />
              <Tooltip formatter={(v) => rupiah(v)} contentStyle={{ borderRadius: 10, fontSize: 12 }} />
              <Bar dataKey="revenue" radius={[6, 6, 0, 0]} fill="#4F46E5" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Distribusi Bahan (30s vs 24s)">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={data.by_fabric} dataKey="pcs" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                {data.by_fabric.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Legend /><Tooltip formatter={(v) => `${numberID(v)} pcs`} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <DimTable title="Per Kategori Umur" rows={data.by_age} />
        <DimTable title="Per Jenis Lengan" rows={data.by_sleeve} />
        <DimTable title="Per Warna" rows={data.by_color} />
        <DimTable title="Per Ukuran" rows={data.by_size} />
      </div>
    </div>
  );
}

const ChartCard = ({ title, children }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
    <h3 className="mb-4 font-display text-base font-semibold text-slate-900">{title}</h3>{children}
  </div>
);

const DimTable = ({ title, rows }) => (
  <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
    <div className="border-b border-slate-100 px-5 py-3"><h3 className="font-display text-base font-semibold text-slate-900">{title}</h3></div>
    <Table>
      <TableHeader><TableRow><TableHead>Nama</TableHead><TableHead className="text-right">PCS</TableHead><TableHead className="text-right">Revenue</TableHead></TableRow></TableHeader>
      <TableBody>
        {(rows || []).length === 0 ? <TableRow><TableCell colSpan={3} className="text-center text-sm text-slate-400 py-6">Belum ada data</TableCell></TableRow> :
          rows.map((r, i) => <TableRow key={i}><TableCell className="font-medium">{r.name || "-"}</TableCell><TableCell className="text-right">{numberID(r.pcs)}</TableCell><TableCell className="text-right">{rupiah(r.revenue)}</TableCell></TableRow>)}
      </TableBody>
    </Table>
  </div>
);
